import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pencil, Save, X, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatsManager = () => {
  const [stats, setStats] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStat, setNewStat] = useState({ value: '', label: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Error",
        description: "Failed to load statistics",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleEdit = (stat) => {
    setEditingId(stat.stat_id);
    setEditData({ ...stat });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (statId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/stats/${statId}`, {
        value: editData.value,
        label: editData.label
      });

      toast({
        title: "Success",
        description: "Statistic updated successfully"
      });

      setEditingId(null);
      fetchStats();
    } catch (error) {
      console.error('Error updating stat:', error);
      toast({
        title: "Error",
        description: "Failed to update statistic",
        variant: "destructive"
      });
    }
  };

  const handleAddStat = async () => {
    if (!newStat.value || !newStat.label) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const nextOrder = stats.length > 0 ? Math.max(...stats.map(s => s.order)) + 1 : 1;
      await axios.post(`${BACKEND_URL}/api/cms/stats`, null, {
        params: {
          value: newStat.value,
          label: newStat.label,
          order: nextOrder
        }
      });

      toast({
        title: "Success",
        description: "Statistic added successfully"
      });

      setShowAddForm(false);
      setNewStat({ value: '', label: '' });
      fetchStats();
    } catch (error) {
      console.error('Error adding stat:', error);
      toast({
        title: "Error",
        description: "Failed to add statistic",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (statId) => {
    if (!window.confirm('Are you sure you want to delete this statistic?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cms/stats/${statId}`);
      toast({
        title: "Success",
        description: "Statistic deleted successfully"
      });
      fetchStats();
    } catch (error) {
      console.error('Error deleting stat:', error);
      toast({
        title: "Error",
        description: "Failed to delete statistic",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading statistics...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Statistics Management</h1>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Statistic
        </Button>
      </div>

      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-xl font-light text-[#2d2d2d] mb-4">Add New Statistic</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Value</label>
              <Input
                value={newStat.value}
                onChange={(e) => setNewStat({ ...newStat, value: e.target.value })}
                placeholder="e.g., $2.5B, 50+, 4"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Label</label>
              <Input
                value={newStat.label}
                onChange={(e) => setNewStat({ ...newStat, label: e.target.value })}
                placeholder="e.g., Projects Completed"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleAddStat} className="bg-green-600 hover:bg-green-700 text-white">
              <Save className="w-4 h-4 mr-2" />
              Add Statistic
            </Button>
            <Button onClick={() => setShowAddForm(false)} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <div key={stat.stat_id} className="bg-white p-6 rounded-lg shadow-lg">
            {editingId === stat.stat_id ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Value</label>
                  <Input
                    value={editData.value}
                    onChange={(e) => setEditData({ ...editData, value: e.target.value })}
                    placeholder="e.g., $2.5B, 50+, 4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Label</label>
                  <Input
                    value={editData.label}
                    onChange={(e) => setEditData({ ...editData, label: e.target.value })}
                    placeholder="e.g., Projects Completed"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleSave(stat.stat_id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleCancel} variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-5xl font-light text-[#0A1A50] mb-4">{stat.value}</div>
                <div className="text-[#2d2d2d] font-light mb-4">{stat.label}</div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(stat)}
                    className="flex-1 bg-[#0A1A50] hover:bg-[#060F2E] text-white"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(stat.stat_id)}
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsManager;